import {v4} from 'uuid';

import Manager from 'coreClasses/Manager';
import ViewportMgr from 'coreClasses/view/ViewportMgr';

import {isServer} from 'coreUtils/utils';

import {
	UIState,
	ERequestClass,
	EInstanceKey,
	EOncloseActions,
	IAddProductToWishlist,
	IAddProductToCart,
	IMoveProductFromBasketToWishlist,
} from 'coreStore/data/ui/types';
import {
	receiveNavigationStack,
	receiveUI,
	receiveReplaceUI,
	selectUI,
	receiveHeader,
	selectRequestClasses,
	receiveCountryConfirmRedirectObj,
	receivePageViewCounter,
	receivePreviousUrl,
	receiveSsrGlobalStyle,
	removeSsrGlobalStyle as removeSsrGlobalStyleAction,
} from 'coreStore/data/ui';
import {EEventSource} from 'coreClasses/events/IEvents';
import {ESearchType} from 'coreClasses/search/ISearch';

/**
 * Represents ScrollLock
 */
class ScrollLock {
	private className: string;
	private rootId: string = null; // TODO: remove rootId
	private counter: number;
	private savedCoords: {x: number; y: number} = null;
	private originalPaddingRight: string = null;
	private uiMgr: UiMgr;
	private openCloseTimeout;

	/**
	 * Scroll locker
	 * @param {string} className - locker classname
	 * @param {string} rootId - root container ID
	 * @param {UiMgr} uiMgr - uiMgr instance
	 */
	constructor(className, rootId, uiMgr) {
		this.className = className;
		this.rootId = rootId;
		this.counter = 0;
		this.uiMgr = uiMgr;
	}

	/**
	 * Locks the page scroll
	 * @param {boolean} saveCoords - if true, current viewPort coordinates are saved
	 * @param {number} openDelay - time to wait before locking the scroll
	 * @returns {void}
	 */
	lock(saveCoords = false, openDelay = 0): void {
		if (this.counter === 0) {
			clearTimeout(this.openCloseTimeout);

			if (openDelay > 0 && isServer) {
				this.openCloseTimeout = setTimeout(() => {
					this.uiMgr.addHtmlCssClasses([this.className]);
				}, openDelay);
			} else {
				this.uiMgr.addHtmlCssClasses([this.className]);
			}

			if (this.rootId && !this.originalPaddingRight && isServer) {
				this.originalPaddingRight = document.body.style.paddingRight;

				// Get the scrollBar width
				const rootElement = document.getElementById(this.rootId); // or root
				const scrollBarWidth = rootElement ? rootElement.offsetWidth - rootElement.scrollWidth : 0;

				// Avoid width reflow
				if (scrollBarWidth) {
					document.body.style.paddingRight = `${scrollBarWidth}px`;
				}
			}
		}

		if (saveCoords && !this.savedCoords && isServer) {
			this.savedCoords = {
				y: document.documentElement.scrollTop,
				x: document.documentElement.scrollLeft,
			};
		}

		this.counter++;
	}

	/**
	 * Unlocks the page scroll
	 * @param {boolean} restoreScrollPosition - if true, current viewPort coordinates restored to
	 * @param {number} closeDelay - time to wait before restoring the scroll
	 * the original ones saved on lock action
	 * @returns {void}
	 */
	unlock(restoreScrollPosition = false, closeDelay = 0): void {
		if (this.counter === 1) {
			clearTimeout(this.openCloseTimeout);

			this.uiMgr.removeHtmlCssClasses([this.className]);

			if (restoreScrollPosition && this.savedCoords) {
				this.openCloseTimeout = setTimeout(() => {
					ViewportMgr.scrollTo(this.savedCoords.y, this.savedCoords.x);
					this.savedCoords = null;
				}, closeDelay);
			}

			if (this.rootId && this.originalPaddingRight) {
				document.body.style.paddingRight = `${this.originalPaddingRight}px`;
			}
		}

		if (this.counter > 0) {
			this.counter--;
		}
	}

	/**
	 * Removes saved coords for scroll restoration
	 * @returns {void}
	 */
	resetSavedCoords(): void {
		this.savedCoords = null;
	}

	/**
	 * Returns true if currently locked
	 * @returns {boolean}
	 */
	isLocked(): boolean {
		return this.counter > 0;
	}
}

/**
 * Represents UiMgr
 */
class UiMgr extends Manager {
	private scrollLocks = new Map();

	/**
	 * Updates the UI store with the given object
	 * The object is deeply merged.
	 * @param {Object} data - the object to dispatch
	 * @returns {void}
	 */
	setUI(data: object): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveUI(data));
	}

	/**
	 * Updates the country selector Obj values
	 * @param {Object} data - the object to dispatch
	 */
	setCountryConfirmRedirectObj(data: UIState['countryConfirmRedirectObj']): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCountryConfirmRedirectObj(data));
	}

	/**
	 * Updates the UI store with the given object
	 * The object is deeply replaced.
	 * @param {Object} data - the object to dispatch
	 * @returns {void}
	 */
	replaceUi(data: object): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveReplaceUI(data));
	}

	/**
	 * Updates the page view counter
	 * @param {number} data - the new counter value
	 * @returns {void}
	 */
	setCounter(data: number): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receivePageViewCounter(data as any));
	}

	/**
	 * Sets the previous URL value.
	 * @param {string} url - the previous URL
	 * @returns {void}
	 */
	setPreviousUrl(url: string): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receivePreviousUrl(url));
	}

	/**
	 * Shows the size selector.
	 * @param {String} productID - Product ID
	 * @param {String} variantID - Variant ID
	 * @param {Element} parent - Element inside which show the flyout
	 * @param {String} parentSelector - CSS selector for inside which show the flyout
	 * @param {String} countryCode - country code used to show the size
	 * @param {Boolean} hidePreorderSizes - hide preorder sizes on flyout
	 * @param {EEventSource} source - Source
	 * @param {String | null} length - Length
	 * @returns {void}
	 */
	showSizeSelector(
		productID: string,
		variantID: string = null,
		parentSelector: string = null,
		parent: Element = null,
		countryCode: string = '',
		hidePreorderSizes: boolean = false,
		source: EEventSource,
		length: string | null = null
	): void {
		if (!parentSelector && parent) {
			if (!parent.id) {
				parent.id = `id-${v4()}`;
			}

			parentSelector = `#${parent.id}`;
		}

		const sizeSelector = {
			productID,
			variantID,
			parentSelector,
			countryCode,
			hidePreorderSizes,
			source,
			length
		};

		this.setUI({sizeSelector});
	}

	/**
	 * Shows the color selector.
	 * @param {String} productID - Product ID
	 * @param {String} variantID - Variant ID
	 * @param {Element} parent - Element inside which show the flyout
	 * @param {String} parentSelector - CSS selector for inside which show the flyout
	 * @param {Boolean} isPriceFiltered - Check if must be filtered by the same price
	 * @returns {void}
	 */
	showColorSelector(
		productID: string,
		variantID: string = null,
		isPriceFiltered: boolean = false,
		parentSelector: string = null,
		parent: Element = null,
	): void {
		if (!parentSelector && parent) {
			if (!parent.id) {
				parent.id = `id-${v4()}`;
			}

			parentSelector = `#${parent.id}`;
		}

		const colorSelector = {
			productID,
			variantID,
			parentSelector,
			isPriceFiltered
		};

		this.setUI({colorSelector});
	}

	/**
	 * Sets the product id selected into size selector
	 * @param  {string} productId - Variant ID
	 */
	setSizeSelectorSelectedPid(productId): void {
		this.setUI({
			sizeSelectorSelectedPID: productId,
			sizeSelector: null,
		});
	}

	/**
	 * Sets the product id selected into color selector
	 * @param  {string} productId - Variant ID
	 */
	setColorSelectorSelectedPid(productId): void {
		this.setUI({
			colorSelectorSelectedPID: productId,
			colorSelector: null,
		});
	}

	/**
	 * Restores the size selector pid
	 * @returns {void}
	 */
	resetSizeSelectorSelectedPid(): void {
		this.setUI({
			sizeSelectorSelectedPID: null,
		});
	}

	/**
	 * Sets the product size
	 * @param {string} productId - Variant ID
	 * @returns {void}
	 */
	setProductSize(productId: string): void {
		this.setUI({
			sizeProductSelected: productId,
		});
	}

	/**
	 * Sets the product color
	 * @param {string} productId - Variant ID
	 * @returns {void}
	 */
	setProductColor(productId: string): void {
		this.setUI({
			colorProductSelected: productId,
		});
	}

	/**
	 * Restores the color selector pid
	 * @returns {void}
	 */
	resetColorSelectorSelectedPid(): void {
		this.setUI({
			colorSelectorSelectedPID: null,
		});
	}

	/**
	 * Reset the country selector Obj
	 * @returns {void}
	 */
	resetCountryConfirmRedirectObj(): void {
		this.setUI({
			countryConfirmRedirectObj: {
				countryCode: '',
				languageCode: '',
			},
		});
	}

	/**
	 * Restores the size selector
	 * @param {Object} data - data to be restored
	 * @returns {void}
	 */
	restoreSizeSelector(data: object): void {
		this.setUI({sizeSelector: data});
	}

	/**
	 * Restores the color selector
	 * @param {Object} data - data to be restored
	 * @returns {void}
	 */
	restoreColorSelector(data: object): void {
		this.setUI({colorSelector: data});
	}

	/**
	 * Hides the size selector
	 * @returns {void}
	 */
	hideSizeSelector(): void {
		this.setUI({sizeSelector: null});
	}

	/**
	 * Hides the color selector
	 * @returns {void}
	 */
	hideColorSelector(): void {
		this.setUI({colorSelector: null});
	}

	/**
	 * Shows the Match it with flyout for the given variation group (base product).
	 * @param {string} variationGroupId - Variation group / base product ID
	 * @returns {void}
	 */
	showMatchItWith(variationGroupId: string): void {
		this.setUI({matchItWith: {variationGroupId}});
	}

	/**
	 * Hides the Match it with flyout state.
	 * @returns {void}
	 */
	hideMatchItWith(): void {
		this.setUI({matchItWith: null});
	}

	/**
	 * Sets the product shown in the Match it with flyout PDP view; null to go back to grid.
	 * @param {string | null} productId - Variation group ID or product ID to show, or null
	 * @param {string} [preselectedVariantId] - Variant already selected in the tile, used as the PDP initial size
	 * @returns {void}
	 */
	setMatchItWithProduct(productId: string | null, preselectedVariantId?: string): void {
		const store = this.managerRepository.getStoreMgr().getStore();
		const ui = selectUI(store.getState());
		const matchItWith = ui?.matchItWith
			? {...ui.matchItWith, selectedProductId: productId, preselectedVariantId: preselectedVariantId ?? undefined}
			: null;
		this.setUI({matchItWith});
	}

	/**
	 * Shows the size guide
	 * @param {String} sizeGuideId - ID of the size guide
	 * @param {Object} itemMeasurements - Measurements
	 * @param {string} fitInformation - Fit Information
	 * @param {Function} onClose - onClose callback
	 * @param {String} tabType - tabType
	 * @param {String} productID - Product ID
	 * @param {String} primaryCategoryName - primaryCategoryName
	 * @returns {void}
	 */
	showSizeGuide(
		sizeGuideId: string,
		itemMeasurements: object,
		onClose: EOncloseActions,
		tabType: string,
		productID: string,
		fitInformation: string = null,
		primaryCategoryName: string = null
	): void {
		this.setUI({
			sizeGuide: {
				sizeGuideId,
				itemMeasurements,
				onClose,
				tabType,
				productID,
				fitInformation,
				primaryCategoryName
			},
		});
	}

	/**
	 * Hides the size guide
	 * @returns {void}
	 */
	hideSizeGuide(): void {
		this.setUI({sizeGuide: null});
	}

	/**
	 * Set visibility of back to top button
	 * @returns {void}
	 */
	showBackToTop(value: boolean): void {
		this.setUI({backToTop: value});
	}

	/**
	 * Shows the notify-me popup
	 * @param {string} productID - Variant ID
	 * @param {string} selectedVariationID - Variation group ID
	 * @returns {void}
	 */
	showNotifyMePopup(productID, selectedVariationID): void {
		this.setUI({
			notifyMe: {
				productID,
				selectedVariationID,
			},
		});
	}

	/**
	 * Hides the size notify-me popup
	 * @returns {void}
	 */
	hideNotifyMePopup(): void {
		this.setUI({notifyMe: null});
	}

	/**
	 * Shows the notify-me flyout
	 * @param {string} productID - Variant ID
	 * @param {string} selectedVariationID - Variation group ID
	 * @returns {void}
	 */
	showNotifyMeFlyout(productID, selectedVariationID): void {
		this.setUI({
			notifyMe: {
				productID,
				selectedVariationID,
			},
		});
	}

	/**
	 * Hides the size notify-me flyout
	 * @returns {void}
	 */
	hideNotifyMeFlyout(): void {
		this.setUI({notifyMe: null});
	}

	/**
	 * Shows the quick-shop flyout
	 * @param {string} productID - Variant ID
	 * @returns {void}
	 */
	showQuickShopFlyout(productID: string): void {
		this.setUI({
			quickShop: {
				productID,
			},
		});
	}

	/**
	 * Hides the size quick-shop flyout
	 * @returns {void}
	 */
	hideQuickShopFlyout(): void {
		this.setUI({quickShop: null});
	}

	/**
	 * Sets the ID of the address to be edited
	 * @param addressId - Address ID
	 * @returns {void}
	 */
	setEditAddressId(addressId: string): void {
		this.setUI({editAddressId: addressId});
	}

	/**
	 * Change the footer "clicked" status.
	 * @param {Boolean} value - on/off
	 * @returns {void}
	 */
	setIsFooterClick(value: boolean): void {
		this.setUI({isFooterClick: value});
	}

	/**
	 * Sets the order to be returned
	 * @param {String} orderNo - the order number
	 * @param {String} zipCode - the order zip code
	 * @returns {void}
	 */
	setSelectedReturnOrder(orderNo: string, zipCode: string = ''): void {
		this.setUI({
			selectedReturnOrder: orderNo,
			selectedReturnOrderZip: zipCode
		});
	}

	/**
	 * Updates the header's settings
	 * @param {Object} data - the new data
	 */
	setHeader(data: UIState['header']): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveHeader(data));
	}

	/**
	 * Updates the footer's settings
	 * @param {Object} data - the new data
	 */
	setFooter(data: UIState['footer']): void {
		this.setUI({footer: data});
	}

	/**
	 * Rotational banner switch
	 * @param boolean - on/off
	 */
	setRotationalBanner(boolean: boolean): void {
		this.setUI({rotationalBanner: boolean});
	}

	/**
	 * Set the scroll to top after loading
	 * @returns {void}
	 */
	setScrollAfterLoading(value: boolean): void {
		this.setUI({scrollAfterLoading: value});
	}

	/**
	 * Adds the provided css classes to the main container
	 * @param {Array<String>} cssClasses
	 * @returns {void}
	 */
	addMainContainerCssClasses(cssClasses: string[]): void {
		const currentClasses = this.managerRepository.getStoreMgr().getStore().getState().ui.mainContainerCssClasses;
		const newClasses = cssClasses.filter((c) => !currentClasses.find((sc) => c === sc));

		if (newClasses.length > 0) {
			this.replaceUi({mainContainerCssClasses: [...currentClasses, ...newClasses]});
		}
	}

	/**
	 * Removes the provided css classes from the main container
	 * @param {Array<String>} cssClasses
	 * @returns {void}
	 */
	removeMainContainerCssClasses(cssClasses: string[]): void {
		const currentClasses = this.managerRepository.getStoreMgr().getStore().getState().ui.mainContainerCssClasses;
		const newClasses = currentClasses.filter((c) => !cssClasses.find((sc) => c === sc));

		if (newClasses.length !== currentClasses.length) {
			this.replaceUi({mainContainerCssClasses: newClasses});
		}
	}

	/**
	 * Updates the main navigation stack
	 * @param {Object} data - the new data
	 * @returns {void}
	 */
	setNavigationStack(data: UIState['navigationStack']): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveNavigationStack(data));
	}

	/**
	 * Flag to indicate if router is in the process of transition to correct locale
	 * F.e. user's region is /en-it/ and he opened a link with /fr-ca/.
	 * @param {boolean} isNavigating - flag for transition process
	 * @returns {void}
	 */
	setNavigatingToCorrectLocale(isNavigating: boolean): void {
		this.setUI({navigatingToCorrectLocale: isNavigating});
	}

	/**
	 * Sets the request class
	 * @param {String[]} requestClasses - the new data
	 * @returns {void}
	 */
	setRequestClasses(requestClasses: string[]): void {
		this.setUI({requestClasses: requestClasses});
	}

	/**
	 * Returns true if the request comes from the splash page
	 * Please look at request_processor.js at brand level.
	 * @returns {Boolean}
	 */
	isSplashPage(): boolean {
		const store = this.managerRepository.getStoreMgr().getStore();
		const ui = selectUI(store.getState());

		return ui.requestClasses.some((r) => r.startsWith(ERequestClass.SPLASH_PAGE_CUSTOMER));
	}

	/**
	 * Returns true if the request comes from IOS APP webview
	 * @returns {Boolean}
	 */
	isIosAppRequest(): boolean {
		const store = this.managerRepository.getStoreMgr().getStore();
		const ui = selectUI(store.getState());

		return ui.requestClasses.indexOf(ERequestClass.IOS_APP) !== -1;
	}

	/**
	 * Adds the provided css classes to the html tag
	 * @param {Array<String>} cssClasses
	 * @returns {void}
	 */
	addHtmlCssClasses(cssClasses: string[]): void {
		const currentClasses = this.managerRepository.getStoreMgr().getStore().getState().ui.htmlCssClasses;
		const newClasses = cssClasses.filter((c) => !currentClasses.includes(c));

		this.replaceUi({htmlCssClasses: [...currentClasses, ...newClasses]});
	}

	/**
	 * Removes the provided css classes from the html tag
	 * @param {Array<String>} cssClasses
	 * @returns {void}
	 */
	removeHtmlCssClasses(cssClasses: string[]): void {
		const currentClasses = this.managerRepository.getStoreMgr().getStore().getState().ui.htmlCssClasses;
		const newClasses = currentClasses.filter((c) => !cssClasses.find((sc) => c === sc));

		this.replaceUi({htmlCssClasses: newClasses});
	}

	/**
	 * Adds a dynamic SSR CSS rule keyed by class name.
	 * If the key already exists the rule is kept and its usage counter is incremented;
	 * a client mount of a rule already counted during SSR claims that usage instead.
	 * @param {string} key - class name
	 * @param {string} value - CSS rule
	 * @returns {void}
	 */
	addSsrGlobalStyle(key: string, value: string): void {
		if (!key || !value) {
			return;
		}

		this.managerRepository.getStoreMgr().getStore().dispatch(receiveSsrGlobalStyle({key, value, isSsr: isServer}));
	}

	/**
	 * Removes a dynamic SSR CSS rule by class name
	 * @param {string} key - class name
	 * @returns {void}
	 */
	removeSsrGlobalStyle(key: string): void {
		if (!key) {
			return;
		}

		this.managerRepository.getStoreMgr().getStore().dispatch(removeSsrGlobalStyleAction(key));
	}

	/**
	 * Stores data about product to add to wishlist
	 * @param {IAddProductToWishlist} item
	 * @param {string|null} item.productId - Variant ID
	 * @param {string} item.variationGroupId - Variation group ID
	 * @param {number} item.quantity - Quantity
	 * @param {string} item.source - Source where Add to wishlist action was triggered from
	 * @returns {void}
	 */
	setProductToAddToWishlist(item: IAddProductToWishlist): void {
		this.setUI({
			addProductToWishlist: {
				...item,
			},
		});
	}

	/**
	 * Restores data about product to add to wishlist
	 * @returns {void}
	 */
	resetProductToAddToWishlist(): void {
		this.replaceUi({
			addProductToWishlist: null,
			sizeSelector: null,
		});
	}

	/**
	 * Stores data about product to add to cart
	 * @param {IAddProductToCart} item
	 * @param {Element} parent - Element inside which show the flyout
	 * @returns {void}
	 */
	setProductToAddToCart(item: IAddProductToCart, parent: Element = null): void {
		let parentSelector = null;

		if (parent) {
			if (!parent.id) {
				parent.id = `id-${v4()}`;
			}

			parentSelector = `#${parent.id}`;
		}

		this.setUI({
			addProductToCart: {
				...item,
				parentSelector,
			},
		});
	}

	/**
	 * Restores data about product to add to cart
	 * @returns {void}
	 */
	resetProductToAddToCart(): void {
		this.replaceUi({
			addProductToCart: null,
		});
	}

	/**
	 * Stores data about product to move from cart to wishlist
	 * @param {IMoveProductFromBasketToWishlist} item
	 * @param {string} item.productId - Variant ID
	 * @param {string} item.variationGroupId - Variation group ID
	 */
	setProductToMoveFromBasketToWishlist(item: IMoveProductFromBasketToWishlist): void {
		this.setUI({
			moveProductFromBasketToWishlist: {
				...item,
			},
		});
	}

	/**
	 * Restores data about product to move from cart to wishlist
	 * @returns {void}
	 */
	resetProductToMoveFromBasketToWishlist(): void {
		this.replaceUi({
			moveProductFromBasketToWishlist: null,
		});
	}

	/**
	 * Returns the ScrollLock instance for the given parameters
	 * @param {string} className - locker classname
	 * @param {string} rootId - root container ID
	 * @returns {ScrollLock}
	 */
	private getScrollLockInstance(className: string, rootId: string): ScrollLock {
		const key = className + rootId;

		if (!this.scrollLocks.has(key)) {
			this.scrollLocks.set(key, new ScrollLock(className, rootId, this));
		}

		return this.scrollLocks.get(key);
	}

	/**
	 * Returns true if given lockInstance is locked
	 * @param {string} className - locker classname
	 * @param {string} rootId - root container ID
	 * @returns {boolean}
	 */
	isScrollLocked(className: string, rootId: string): boolean {
		const scrollLock = this.getScrollLockInstance(className, rootId);

		return scrollLock.isLocked();
	}

	/**
	 * Locks the viewport for the given parameters
	 * @param {string} className - locker classname
	 * @param {string} rootId - root container ID
	 * @param {string} saveScrollPosition - if true, current viewport scroll position is saved for future restorations
	 * @param {number} lockDelay - if > 0, it is used as delay for scroll lock
	 * @returns {void}
	 */
	lockScroll(className: string, rootId: string, saveScrollPosition: boolean = false, lockDelay: number = 0): void {
		const scrollLock = this.getScrollLockInstance(className, rootId);

		scrollLock.lock(saveScrollPosition, lockDelay);
	}

	/**
	 * Unlocks the viewport for the given parameters
	 * @param {string} className - locker classname
	 * @param {string} rootId - root container ID
	 * @param {boolean} restoreScrollPosition - if true, restores the last saved position before the screen lock
	 * @param {number} closeDelay - if > 0, it is used as delay for scroll restoring
	 * @returns {void}
	 */
	unlockScroll(
		className: string,
		rootId: string,
		restoreScrollPosition: boolean = false,
		closeDelay: number = 0,
	): void {
		const scrollLock = this.getScrollLockInstance(className, rootId);

		scrollLock.unlock(restoreScrollPosition, closeDelay);
	}

	/**
	 * Unlocks the viewport for the given parameters
	 * @param {string} className - locker classname
	 * @param {string} rootId - root container ID
	 * @returns {void}
	 */
	resetLockScrollSavedCoords(className: string, rootId: string): void {
		const scrollLock = this.getScrollLockInstance(className, rootId);

		scrollLock.resetSavedCoords();
	}

	/**
	 * Returns true if request class includes CMS_PREVIEW
	 * @returns {boolean}
	 */
	isCMSPreview(): boolean {
		return (
			selectRequestClasses(this.managerRepository.getStoreMgr().getStore().getState(), 'requestClasses').indexOf(
				ERequestClass.CMS_PREVIEW,
			) !== -1
		);
	}

	/**
	 * Returns true if request class includes LIVE_PREVIEW
	 * @returns {boolean}
	 */
	isLivePreview(): boolean {
		return (
			selectRequestClasses(this.managerRepository.getStoreMgr().getStore().getState(), 'requestClasses').indexOf(
				ERequestClass.LIVE_PREVIEW
			) !== -1
		);
	}

	/**
	 * Returns instance key
	 * @returns {String}
	 */
	getInstanceKey(): EInstanceKey {
		const requestClasses = selectRequestClasses(this.managerRepository.getStoreMgr().getStore().getState(), 'requestClasses');
		let instanceKey: EInstanceKey;

		if (requestClasses.includes(ERequestClass.INSTANCE_PRODUCTION)) {
			instanceKey = EInstanceKey.PRODUCTION;
		} else if (requestClasses.includes(ERequestClass.INSTANCE_STAGING)) {
			instanceKey = EInstanceKey.STAGING;
		} else if (requestClasses.includes(ERequestClass.INSTANCE_DEVELOP)) {
			instanceKey = EInstanceKey.DEVELOP;
		} else if (requestClasses.includes(ERequestClass.INSTANCE_SANDBOX)) {
			instanceKey = EInstanceKey.SANDBOX;
		} else {
			instanceKey = EInstanceKey.LOCAL;
		}

		return instanceKey;
	}

	/**
	 * Returns true if on remote instance
	 * @returns {Boolean}
	 */
	isRemote(): boolean {
		const requestClasses = selectRequestClasses(this.managerRepository.getStoreMgr().getStore().getState(), 'requestClasses');

		return requestClasses.includes(ERequestClass.REMOTE);
	}

	/**
	 * Returns true if inside a CORS Request
	 * @returns {Boolean}
	 */
	isCORSRequest(): boolean {
		const requestClasses = selectRequestClasses(this.managerRepository.getStoreMgr().getStore().getState(), 'requestClasses');

		return requestClasses.includes(ERequestClass.CORS);
	}

	/**
	 * Set the search config
	 * @param searchInstanceKey - Search instance key
	 * @param searchType - Search type
	 * @returns {void}
	 */
	setSearchConfig(searchInstanceKey: string, searchType: ESearchType): void {
		this.setUI({
			searchConfig: {
				searchInstanceKey,
				searchType,
			}
		});
	}

	/**
	 * Reset the search config
	 * @returns {void}
	 */
	resetSearchConfig(): void {
		this.setUI({
			searchConfig: {
				searchInstanceKey: undefined,
				searchType: undefined,
			}
		});
	}

	/**
	 * Set the emitted event count
	 * @param pageViewCounter - Event count
	 * @returns {void}
	 */
	setEventCount(pageViewCounter: number): void {
		this.setUI({pageViewCounter: pageViewCounter});
	}
}

export default UiMgr;
